import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { ErreurApi } from '../../../api/client.js';
import { CIBLE_MIN, COULEURS, styleBouton, styleBoutonPrincipal } from '../../../app/styles.js';
import type { CompteRendu, NatureCompteRendu, SourceComptesRendus } from './source.js';

/**
 * Onglet « Comptes rendus » du dossier (PARITE-4) : disposition de la démo (isabelle/ComptesRendus) branchée sur l'API réelle.
 * À faire → générer depuis le texte collé (toujours « à vérifier ») → relire, corriger → valider. Interne au cabinet.
 * `peutEcrire` et `peutValider` viennent des permissions de la session (serveur) ; l'écran ne décide d'aucun droit.
 */
export interface PropsVueComptesRendus {
  source: SourceComptesRendus;
  peutEcrire: boolean;
  peutValider: boolean;
}

const NATURES: Readonly<Record<NatureCompteRendu, string>> = { entretien: 'Entretien', appel: 'Appel' };
const RAISON_ECRIRE = 'Votre rôle ne permet pas d’ajouter ou de générer un compte rendu.';
const RAISON_VALIDER = 'Seule la dirigeante valide un compte rendu.';

const messageDe = (e: unknown): string =>
  e instanceof ErreurApi ? e.messageUtilisateur : e instanceof Error ? e.message : 'Une erreur est survenue. Réessayez.';
const dateFr = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'America/Guadeloupe', day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: '1px solid #E2E1EA',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
};
const titre: CSSProperties = { margin: 0, fontSize: 18, color: COULEURS.marine };
const discret: CSSProperties = { color: COULEURS.ardoise, fontSize: 13 };
const champ: CSSProperties = {
  font: 'inherit',
  minHeight: CIBLE_MIN,
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.bordure}`,
  width: '100%',
  boxSizing: 'border-box',
};
const ligne: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  padding: '8px 0',
  borderTop: '1px solid #EEEDF3',
};
const actions: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8 };

function Etiquette({ pour, children }: { pour: string; children: ReactNode }) {
  return (
    <label htmlFor={pour} style={{ fontWeight: 600, fontSize: 14 }}>
      {children}
    </label>
  );
}

function Alerte({ message }: { message: string | null }) {
  return message ? (
    <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
      {message}
    </p>
  ) : null;
}

type Panneau = { type: 'ajout' } | { type: 'coller'; cr: CompteRendu } | { type: 'relire'; cr: CompteRendu } | null;

export function VueComptesRendus({ source, peutEcrire, peutValider }: PropsVueComptesRendus) {
  const [liste, setListe] = useState<CompteRendu[] | null>(null);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [panneau, setPanneau] = useState<Panneau>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [titreSaisi, setTitreSaisi] = useState('');
  const [nature, setNature] = useState<NatureCompteRendu>('appel');
  const [texteSource, setTexteSource] = useState('');
  const [texteRelu, setTexteRelu] = useState('');

  const charger = useCallback(async () => {
    setErreurLecture(null);
    try {
      setListe(await source.lister());
    } catch (e) {
      setErreurLecture(messageDe(e));
    }
  }, [source]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const ouvrir = (p: Panneau) => {
    setErreur(null);
    setPanneau(p);
    if (p?.type === 'ajout') {
      setTitreSaisi('');
      setTexteSource('');
    }
    if (p?.type === 'coller') setTexteSource('');
    if (p?.type === 'relire') setTexteRelu(p.cr.body ?? '');
  };

  async function agir(travail: () => Promise<void>): Promise<void> {
    setEnCours(true);
    setErreur(null);
    try {
      await travail();
    } catch (e) {
      setErreur(messageDe(e));
    } finally {
      setEnCours(false);
    }
  }

  const ajouter = () =>
    agir(async () => {
      await source.creer({ kind: nature, title: titreSaisi, ...(texteSource.trim() ? { sourceText: texteSource } : {}) });
      setPanneau(null);
      await charger();
    });

  const generer = (cr: CompteRendu, texte?: string) =>
    agir(async () => {
      const genere = texte === undefined ? await source.generer(cr.id) : await source.generer(cr.id, texte);
      ouvrir({ type: 'relire', cr: genere });
      await charger();
    });

  const demanderGeneration = (cr: CompteRendu) => {
    if (cr.sourceText?.trim()) void generer(cr);
    else ouvrir({ type: 'coller', cr });
  };

  const corriger = (cr: CompteRendu) =>
    agir(async () => {
      const corrige = await source.corriger(cr.id, texteRelu, cr.rowVersion);
      setPanneau({ type: 'relire', cr: corrige });
      await charger();
    });

  const valider = (cr: CompteRendu) =>
    agir(async () => {
      await source.valider(cr.id, cr.rowVersion);
      setPanneau(null);
      await charger();
    });

  if (erreurLecture) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <Alerte message={erreurLecture} />
        <button type="button" style={styleBouton} onClick={() => void charger()}>
          Réessayer
        </button>
      </div>
    );
  }
  if (liste === null) return <p role="status">Chargement des comptes rendus…</p>;

  const aFaire = liste.filter((c) => c.status === 'a_faire');
  const aRelire = liste.filter((c) => c.status === 'a_relire');
  const valides = liste.filter((c) => c.status === 'valide');
  const meta = (c: CompteRendu) => `${NATURES[c.kind]} · ajouté le ${dateFr(c.createdAt)} par ${c.createdByDisplayName}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <p style={{ margin: 0, ...discret, fontSize: 14 }}>
          Interne au cabinet : un compte rendu n’est jamais transmis aux héritiers, au mandant ni à l’étude.
        </p>
        <button
          type="button"
          style={styleBoutonPrincipal}
          disabled={!peutEcrire}
          title={peutEcrire ? undefined : RAISON_ECRIRE}
          onClick={() => ouvrir({ type: 'ajout' })}
        >
          Ajouter un compte rendu à faire
        </button>
      </div>

      {panneau?.type === 'ajout' ? (
        <section aria-label="Nouveau compte rendu" style={carte}>
          <h2 style={titre}>Nouveau compte rendu à faire</h2>
          <Etiquette pour="cr-titre">Titre de l’entretien ou de l’appel</Etiquette>
          <input
            id="cr-titre"
            type="text"
            maxLength={200}
            value={titreSaisi}
            placeholder="Par exemple : Entretien avec Nadia C."
            onChange={(e) => setTitreSaisi(e.target.value)}
            style={champ}
          />
          <Etiquette pour="cr-nature">Nature</Etiquette>
          <select id="cr-nature" value={nature} onChange={(e) => setNature(e.target.value as NatureCompteRendu)} style={champ}>
            <option value="appel">Appel</option>
            <option value="entretien">Entretien</option>
          </select>
          <Etiquette pour="cr-texte-initial">Texte de l’entretien (facultatif maintenant)</Etiquette>
          <textarea id="cr-texte-initial" rows={4} value={texteSource} onChange={(e) => setTexteSource(e.target.value)} style={champ} />
          <Alerte message={erreur} />
          <div style={actions}>
            <button type="button" style={styleBouton} onClick={() => setPanneau(null)}>
              Annuler
            </button>
            <button type="button" style={styleBoutonPrincipal} disabled={enCours || !titreSaisi.trim()} onClick={() => void ajouter()}>
              Ajouter
            </button>
          </div>
        </section>
      ) : null}

      {panneau?.type === 'coller' ? (
        <section aria-label="Texte de l’entretien" style={carte}>
          <h2 style={titre}>{`Texte de l’entretien : ${panneau.cr.title}`}</h2>
          <p style={{ margin: 0, ...discret }}>
            Ce compte rendu n’a pas encore de texte. Collez la transcription de l’entretien : le compte rendu est généré à partir d’elle, à
            relire avant tout usage.
          </p>
          <Etiquette pour="cr-texte-source">Texte de l’entretien</Etiquette>
          <textarea id="cr-texte-source" rows={8} value={texteSource} onChange={(e) => setTexteSource(e.target.value)} style={champ} />
          <Alerte message={erreur} />
          <div style={actions}>
            <button type="button" style={styleBouton} onClick={() => setPanneau(null)}>
              Annuler
            </button>
            <button
              type="button"
              style={styleBoutonPrincipal}
              disabled={enCours || !texteSource.trim()}
              onClick={() => void generer(panneau.cr, texteSource)}
            >
              Générer à partir de ce texte
            </button>
          </div>
        </section>
      ) : null}

      {panneau?.type === 'relire' ? (
        <section aria-label="Relecture du compte rendu" style={carte}>
          <h2 style={titre}>{panneau.cr.status === 'valide' ? `Lire : ${panneau.cr.title}` : `Relire : ${panneau.cr.title}`}</h2>
          <p style={{ margin: 0, ...discret }}>
            {panneau.cr.status === 'valide'
              ? `Validé le ${dateFr(panneau.cr.validatedAt)} par ${panneau.cr.validatedByDisplayName ?? ''} : il ne se modifie plus.`
              : 'Généré automatiquement : à vérifier, puis à valider.'}
          </p>
          <Etiquette pour="cr-relecture">Compte rendu</Etiquette>
          <textarea
            id="cr-relecture"
            rows={14}
            value={texteRelu}
            readOnly={panneau.cr.status !== 'a_relire' || !peutEcrire}
            onChange={(e) => setTexteRelu(e.target.value)}
            style={champ}
          />
          {panneau.cr.status === 'a_relire' && !peutValider ? <p style={{ margin: 0, ...discret }}>{RAISON_VALIDER}</p> : null}
          {panneau.cr.status === 'a_relire' && peutValider && texteRelu !== (panneau.cr.body ?? '') ? (
            <p style={{ margin: 0, ...discret }}>Enregistrez vos corrections avant de valider : on ne valide que le texte relu.</p>
          ) : null}
          <Alerte message={erreur} />
          <div style={actions}>
            <button type="button" style={styleBouton} onClick={() => setPanneau(null)}>
              Fermer
            </button>
            {panneau.cr.status === 'a_relire' ? (
              <>
                <button
                  type="button"
                  style={styleBouton}
                  disabled={enCours || !peutEcrire || !texteRelu.trim()}
                  onClick={() => void corriger(panneau.cr)}
                >
                  Enregistrer les corrections
                </button>
                <button
                  type="button"
                  style={styleBoutonPrincipal}
                  disabled={enCours || !peutValider || texteRelu !== (panneau.cr.body ?? '')}
                  title={peutValider ? undefined : RAISON_VALIDER}
                  onClick={() => void valider(panneau.cr)}
                >
                  Valider le compte rendu
                </button>
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      <section aria-label="Comptes rendus à faire" style={carte}>
        <h2 style={titre}>{`À faire (${aFaire.length})`}</h2>
        {aFaire.length === 0 ? (
          <p style={{ margin: 0, ...discret }}>Aucun compte rendu à faire. Un entretien ou un appel tenu s’ajoute ici.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {aFaire.map((c) => (
              <li key={c.id} style={ligne}>
                <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <strong>{c.title}</strong>
                  <br />
                  <span style={discret}>{meta(c)}</span>
                </span>
                <button
                  type="button"
                  style={styleBoutonPrincipal}
                  disabled={enCours || !peutEcrire}
                  title={peutEcrire ? undefined : RAISON_ECRIRE}
                  aria-label={`Générer le compte rendu : ${c.title}`}
                  onClick={() => demanderGeneration(c)}
                >
                  Générer le compte rendu
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Comptes rendus à relire" style={carte}>
        <h2 style={titre}>{`À relire (${aRelire.length})`}</h2>
        {aRelire.length === 0 ? (
          <p style={{ margin: 0, ...discret }}>Aucun compte rendu à relire.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {aRelire.map((c) => (
              <li key={c.id} style={ligne}>
                <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <strong>{c.title}</strong>
                  <br />
                  <span style={discret}>{`${NATURES[c.kind]} · généré le ${dateFr(c.generatedAt)} · à vérifier`}</span>
                </span>
                <button
                  type="button"
                  style={styleBouton}
                  aria-label={`Relire et valider : ${c.title}`}
                  onClick={() => ouvrir({ type: 'relire', cr: c })}
                >
                  Relire et valider
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Comptes rendus validés" style={carte}>
        <h2 style={titre}>{`Validés (${valides.length})`}</h2>
        {valides.length === 0 ? (
          <p style={{ margin: 0, ...discret }}>Aucun compte rendu validé pour l’instant.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {valides.map((c) => (
              <li key={c.id} style={ligne}>
                <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <strong>{c.title}</strong>
                  <br />
                  <span
                    style={discret}
                  >{`${NATURES[c.kind]} · validé le ${dateFr(c.validatedAt)} par ${c.validatedByDisplayName ?? ''}`}</span>
                </span>
                <button
                  type="button"
                  style={styleBouton}
                  aria-label={`Lire : ${c.title}`}
                  onClick={() => ouvrir({ type: 'relire', cr: c })}
                >
                  Lire
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
